/**
 * PsicoSalud — Presentación del análisis de una nota clínica (Módulo 5).
 *
 * Muestra siempre la evidencia textual que originó cada indicador: sin
 * ella el profesional no puede validar ni descartar la señal, y el
 * componente dejaría de ser apoyo a la decisión para convertirse en una
 * caja negra.
 *
 * También lista los indicadores descartados por negación ("no refiere
 * ideación suicida"), porque saber qué se evaluó y se descartó es parte
 * de la información clínica.
 */

import { useTranslation } from 'react-i18next';

import type { NlpAnalysis, RiskIndicator } from '@/types';

const PRIORITY_BADGE: Record<string, string> = {
  NINGUNA: 'badge-neutral',
  BAJA: 'badge-info',
  MEDIA: 'badge-warning',
  ALTA: 'badge-critical',
};

const TONE_BADGE: Record<string, string> = {
  muy_negativa: 'badge-critical',
  negativa: 'badge-warning',
  neutra: 'badge-neutral',
  positiva: 'badge-success',
  muy_positiva: 'badge-success',
};

export default function NoteAnalysisPanel({ analysis }: { analysis: NlpAnalysis }) {
  const { t } = useTranslation();
  const risk = analysis.indicadores_riesgo;
  const affect = analysis.analisis_afectivo;
  const categories = Object.entries(analysis.categorias_sintomaticas);

  return (
    <div
      className="is-ai-bordered"
      style={{ padding: 'var(--espacio-5)', marginTop: '0.6rem' }}
      data-cy="note-analysis"
    >
      <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--espacio-2)' }}>
          <span className="is-ai-icon" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10 2L11.6 7.4L17 9L11.6 10.6L10 16L8.4 10.6L3 9L8.4 7.4L10 2Z"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              <circle cx="16" cy="4" r="1.5" fill="currentColor" />
              <circle cx="4" cy="16" r="1.5" fill="currentColor" />
            </svg>
          </span>
          <h3 style={{ margin: 0 }}>{t('analytics.nlpAnalysis')}</h3>
        </div>
        <span className={`badge ${PRIORITY_BADGE[risk.prioridad_revision] ?? 'badge-neutral'}`}>
          {t('analytics.reviewPriority')}: {t(`analytics.priorities.${risk.prioridad_revision}`)}
        </span>
      </div>

      <p className="t-pequeno">
        <strong>{t('analytics.affectiveTone')}:</strong>{' '}
        <span className={`badge ${TONE_BADGE[affect.etiqueta] ?? 'badge-neutral'}`}>
          {affect.etiqueta.replace('_', ' ')}
        </span>{' '}
        <span className="t-suave">({affect.polaridad.toFixed(2)})</span>
      </p>

      {categories.length > 0 && (
        <p className="t-pequeno">
          <strong>{t('analytics.symptomCategories')}:</strong>{' '}
          {categories.map(([key, value]) => (
            <span key={key} className="badge badge-info" style={{ marginRight: '0.3rem' }}>
              {t(`analytics.categories.${key}`, { defaultValue: key })} ({value.frecuencia})
            </span>
          ))}
        </p>
      )}

      {risk.indicadores_activos.length > 0 && (
        <>
          <h4 className="t-pequeno">{t('analytics.riskIndicators')}</h4>
          <ul className="t-pequeno" data-cy="note-analysis-indicators">
            {risk.indicadores_activos.map((indicator, index) => (
              <IndicatorItem key={`${indicator.indicador}-${index}`} indicator={indicator} />
            ))}
          </ul>
        </>
      )}

      {risk.indicadores_negados.length > 0 && (
        <>
          <h4 className="t-pequeno t-suave">{t('analytics.negatedIndicators')}</h4>
          <ul className="t-pequeno t-suave">
            {risk.indicadores_negados.map((indicator, index) => (
              <li key={`${indicator.indicador}-neg-${index}`}>
                {t(`analytics.indicators.${indicator.indicador}`, {
                  defaultValue: indicator.indicador,
                })}
                {' — '}
                <em>“{indicator.evidencia}”</em>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="t-pequeno t-suave" style={{ marginBottom: 0 }}>
        {analysis.metricas_texto.palabras} palabras · {analysis.metricas_texto.oraciones} oraciones ·{' '}
        {t('common.date')}: {analysis.modelo_linguistico}
      </p>

      <div className="ai-disclaimer" data-cy="ai-disclaimer">
        {analysis.disclaimer}
        {analysis.disclaimer_riesgo && (
          <>
            <br />
            {analysis.disclaimer_riesgo}
          </>
        )}
      </div>
    </div>
  );
}

function IndicatorItem({ indicator }: { indicator: RiskIndicator }) {
  const { t } = useTranslation();
  return (
    <li>
      <strong>
        {t(`analytics.indicators.${indicator.indicador}`, { defaultValue: indicator.indicador })}
      </strong>{' '}
      <span className="t-suave">({indicator.termino})</span>
      <br />
      <span className="t-suave">{t('analytics.evidence')}: </span>
      <em>“{indicator.evidencia}”</em>
    </li>
  );
}
